import { formatShortMoney } from "@/lib/formatters";
import { useUrlState } from "@/lib/useUrlState";
import { useState, useEffect, useCallback, useMemo } from "react";
import SearchBar from "@/components/SearchBar";
import { Pill } from "@/components/FilterPills";
import Segmented from "@/components/Segmented";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Plus, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Ban, ArrowRight, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

const RATING_OPTIONS = ["A", "B", "C", "D"];

const RATING_COLORS = {
  A: "bg-emerald-100 text-emerald-700 border-emerald-200",
  B: "bg-blue-100 text-blue-700 border-blue-200",
  C: "bg-amber-100 text-amber-700 border-amber-200",
  D: "bg-red-100 text-red-700 border-red-200",
};

const TAB_OPTIONS = ["accounts", "no-bid"];

function formatRevenue(val) {
  if (val == null || val === "") return "--";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

function SortHeader({ column, label }) {
  const sorted = column.getIsSorted();
  return (
    <button className="flex items-center gap-1.5 group" onClick={() => column.toggleSorting(sorted === "asc")} data-testid={`account-sort-${column.id}`}>
      <span>{label}</span>
      <span className="opacity-0 group-hover:opacity-100 transition-opacity">
        {sorted === "asc" ? <ArrowUp className="h-3 w-3" /> : sorted === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 text-slate-400" />}
      </span>
    </button>
  );
}

export default function AccountsPage() {
  const [view, setView] = useUrlState("view", "assigned");   // view pills (UI_PATTERNS.md §4)
  const [accounts, setAccounts] = useState([]);
  const [unassignedAccounts, setUnassignedAccounts] = useState([]);
  const [noBidList, setNoBidList] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [unassignedSearchQuery, setUnassignedSearchQuery] = useState("");
  const [amFilter, setAmFilter] = useState("ALL");
  const [ratingFilter, setRatingFilter] = useState("ALL");
  const [editingCell, setEditingCell] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [unassignedSorting, setUnassignedSorting] = useState([]);
  const [noBidSorting, setNoBidSorting] = useState([]);
  const [noBidSearchQuery, setNoBidSearchQuery] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [assignedAccts, unassignedAccts, mgrs, noBids] = await Promise.all([
        api.getAccounts(true),
        api.getAccounts(false),
        api.getAccountManagers(),
        api.getNoBidList(),
      ]);
      setAccounts(assignedAccts);
      setUnassignedAccounts(unassignedAccts);
      setManagers(mgrs);
      setNoBidList(noBids);
    } catch (e) {
      console.error("Failed to fetch accounts:", e);
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const managerNames = useMemo(() => {
    const names = managers.map(m => m.name);
    if (!names.includes("Unassigned")) names.unshift("Unassigned");
    return names;
  }, [managers]);

  const filteredAccounts = useMemo(() => {
    let result = accounts;
    if (amFilter !== "ALL") result = result.filter(a => a.account_manager === amFilter);
    if (ratingFilter !== "ALL") result = result.filter(a => a.rating === ratingFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a => (a.client || "").toLowerCase().includes(q));
    }
    return result;
  }, [accounts, amFilter, ratingFilter, searchQuery]);

  const filteredUnassignedAccounts = useMemo(() => {
    if (!unassignedSearchQuery) return unassignedAccounts;
    const q = unassignedSearchQuery.toLowerCase();
    return unassignedAccounts.filter(a => (a.client || "").toLowerCase().includes(q));
  }, [unassignedAccounts, unassignedSearchQuery]);

  // Sum totals from ALL assigned accounts (not just filtered)
  // abs_revenue is now computed by the backend
  const totals = useMemo(() => {
    const gcTotal = accounts.reduce((sum, a) => sum + (a.revenue || 0), 0);
    const absTotal = accounts.reduce((sum, a) => sum + (a.abs_revenue || 0), 0);
    return { gcTotal, absTotal };
  }, [accounts]);

  // Sum totals for unassigned accounts
  const unassignedTotals = useMemo(() => {
    const gcTotal = unassignedAccounts.reduce((sum, a) => sum + (a.revenue || 0), 0);
    const absTotal = unassignedAccounts.reduce((sum, a) => sum + (a.abs_revenue || 0), 0);
    return { gcTotal, absTotal };
  }, [unassignedAccounts]);

  const handleAddAccount = async (assigned = true) => {
    try {
      const newAcct = await api.createAccount({ client: "", account_manager: "Unassigned", rating: "", revenue: null, assigned });
      if (assigned) {
        setAccounts(prev => [newAcct, ...prev]);
      } else {
        setUnassignedAccounts(prev => [newAcct, ...prev]);
      }
      setEditingCell({ rowId: newAcct.id, field: "client" });
      toast.success("Account added");
    } catch (e) {
      toast.error("Failed to add account");
    }
  };

  const handleUpdate = async (accountId, field, value) => {
    try {
      const updated = await api.updateAccount(accountId, { [field]: value });
      // Update in whichever list contains this account
      setAccounts(prev => prev.map(a => a.id === accountId ? updated : a));
      setUnassignedAccounts(prev => prev.map(a => a.id === accountId ? updated : a));
      setEditingCell(null);
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (accountId, isUnassigned = false) => {
    try {
      await api.deleteAccount(accountId);
      if (isUnassigned) {
        setUnassignedAccounts(prev => prev.filter(a => a.id !== accountId));
      } else {
        setAccounts(prev => prev.filter(a => a.id !== accountId));
      }
      toast.success("Account deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleAssignAccount = async (accountId) => {
    try {
      const updated = await api.assignAccount(accountId);
      setUnassignedAccounts(prev => prev.filter(a => a.id !== accountId));
      setAccounts(prev => [updated, ...prev]);
      toast.success("Moved to Assigned");
    } catch (e) {
      toast.error("Failed to assign account");
    }
  };

  const handleUnassignAccount = async (accountId) => {
    try {
      const updated = await api.unassignAccount(accountId);
      setAccounts(prev => prev.filter(a => a.id !== accountId));
      setUnassignedAccounts(prev => [updated, ...prev]);
      toast.success("Moved to Unassigned");
    } catch (e) {
      toast.error("Failed to unassign account");
    }
  };

  // --- No Bid List Handlers ---
  const handleAddNoBid = async () => {
    try {
      const newItem = await api.createNoBid({ client: "", reason: "" });
      setNoBidList(prev => [newItem, ...prev]);
      setEditingCell({ rowId: newItem.id, field: "client" });
      toast.success("No bid entry added");
    } catch (e) {
      toast.error("Failed to add no bid entry");
    }
  };

  const handleUpdateNoBid = async (itemId, field, value) => {
    try {
      const updated = await api.updateNoBid(itemId, { [field]: value });
      setNoBidList(prev => prev.map(item => item.id === itemId ? updated : item));
      setEditingCell(null);
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDeleteNoBid = async (itemId) => {
    try {
      await api.deleteNoBid(itemId);
      setNoBidList(prev => prev.filter(item => item.id !== itemId));
      toast.success("No bid entry deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const filteredNoBidList = useMemo(() => {
    if (!noBidSearchQuery) return noBidList;
    const q = noBidSearchQuery.toLowerCase();
    return noBidList.filter(item => 
      (item.client || "").toLowerCase().includes(q) || 
      (item.reason || "").toLowerCase().includes(q)
    );
  }, [noBidList, noBidSearchQuery]);

  const columns = useMemo(() => [
    {
      accessorKey: "client",
      header: ({ column }) => <SortHeader column={column} label="Client" />,
      cell: ({ row }) => {
        const acct = row.original;
        const isEditing = editingCell?.rowId === acct.id && editingCell?.field === "client";
        if (isEditing) {
          return (
            <Input defaultValue={acct.client || ""} autoFocus className="h-7 text-xs w-full" data-testid={`client-input-${row.index}`}
              onBlur={(e) => handleUpdate(acct.id, "client", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUpdate(acct.id, "client", e.target.value); if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="font-medium text-slate-900 cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: acct.id, field: "client" })} data-testid={`cell-client-${row.index}`}>
            {acct.client || <span className="text-slate-400 italic">Enter client name</span>}
          </div>
        );
      },
      size: 220,
    },
    {
      accessorKey: "account_manager",
      header: ({ column }) => <SortHeader column={column} label="Account Manager" />,
      cell: ({ row }) => {
        const acct = row.original;
        return (
          <div data-testid={`cell-account-am-${row.index}`}>
            <Select value={acct.account_manager || "Unassigned"} onValueChange={(val) => handleUpdate(acct.id, "account_manager", val)}>
              <SelectTrigger className="h-7 border-0 bg-transparent p-0 shadow-none focus:ring-0 w-auto text-sm" data-testid={`account-am-select-${row.index}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {managerNames.map((name, idx) => (
                  <SelectItem key={`${name}-${idx}`} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      },
      size: 150,
    },
    {
      accessorKey: "rating",
      header: ({ column }) => <SortHeader column={column} label="Rating" />,
      cell: ({ row }) => {
        const acct = row.original;
        return (
          <div data-testid={`cell-rating-${row.index}`}>
            <Select value={acct.rating || "none"} onValueChange={(val) => handleUpdate(acct.id, "rating", val === "none" ? "" : val)}>
              <SelectTrigger className="h-7 border-0 bg-transparent p-0 shadow-none focus:ring-0 w-auto" data-testid={`rating-select-${row.index}`}>
                <SelectValue>
                  {acct.rating ? (
                    <Badge className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-semibold border shadow-none", RATING_COLORS[acct.rating] || "bg-slate-100 text-slate-600")}>
                      {acct.rating}
                    </Badge>
                  ) : <span className="text-slate-400 text-sm">Set rating</span>}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No rating</SelectItem>
                {RATING_OPTIONS.map(r => (
                  <SelectItem key={r} value={r} data-testid={`rating-option-${r.toLowerCase()}`}>
                    <Badge className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-semibold border shadow-none", RATING_COLORS[r])}>{r}</Badge>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      },
      size: 90,
    },
    {
      accessorKey: "revenue",
      header: ({ column }) => <SortHeader column={column} label="GC Revenue" />,
      cell: ({ row }) => {
        const acct = row.original;
        const isEditing = editingCell?.rowId === acct.id && editingCell?.field === "revenue";
        if (isEditing) {
          return (
            <Input type="number" defaultValue={acct.revenue ?? ""} autoFocus className="h-7 text-xs w-32 text-right" data-testid={`revenue-input-${row.index}`}
              onBlur={(e) => { const v = parseFloat(e.target.value); handleUpdate(acct.id, "revenue", isNaN(v) ? null : v); }}
              onKeyDown={(e) => { if (e.key === "Enter") { const v = parseFloat(e.target.value); handleUpdate(acct.id, "revenue", isNaN(v) ? null : v); } if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="text-right tabular-nums text-slate-700 font-medium cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: acct.id, field: "revenue" })} data-testid={`cell-revenue-${row.index}`}>
            {acct.revenue != null ? formatRevenue(acct.revenue) : <span className="text-slate-400 italic">Set revenue</span>}
          </div>
        );
      },
      size: 120,
    },
    {
      accessorKey: "hit_rate",
      header: ({ column }) => <SortHeader column={column} label="Hit Rate" />,
      cell: ({ row }) => {
        const acct = row.original;
        const isEditing = editingCell?.rowId === acct.id && editingCell?.field === "hit_rate";
        const val = acct.hit_rate ?? 0.25;
        if (isEditing) {
          return (
            <Input type="number" step="0.01" min="0" max="1" defaultValue={val} autoFocus className="h-7 text-xs w-20 text-right" data-testid={`hit-rate-input-${row.index}`}
              onBlur={(e) => { const v = parseFloat(e.target.value); handleUpdate(acct.id, "hit_rate", isNaN(v) ? 0.25 : Math.min(1, Math.max(0, v))); }}
              onKeyDown={(e) => { if (e.key === "Enter") { const v = parseFloat(e.target.value); handleUpdate(acct.id, "hit_rate", isNaN(v) ? 0.25 : Math.min(1, Math.max(0, v))); } if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="text-right tabular-nums text-slate-700 font-medium cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: acct.id, field: "hit_rate" })} data-testid={`cell-hit-rate-${row.index}`}>
            {(val * 100).toFixed(0)}%
          </div>
        );
      },
      size: 80,
    },
    {
      accessorKey: "pct_contract",
      header: ({ column }) => <SortHeader column={column} label="% Contract" />,
      cell: ({ row }) => {
        const acct = row.original;
        const isEditing = editingCell?.rowId === acct.id && editingCell?.field === "pct_contract";
        const val = acct.pct_contract ?? 0.005;
        if (isEditing) {
          return (
            <Input type="number" step="0.001" min="0" max="1" defaultValue={val} autoFocus className="h-7 text-xs w-20 text-right" data-testid={`pct-contract-input-${row.index}`}
              onBlur={(e) => { const v = parseFloat(e.target.value); handleUpdate(acct.id, "pct_contract", isNaN(v) ? 0.005 : Math.min(1, Math.max(0, v))); }}
              onKeyDown={(e) => { if (e.key === "Enter") { const v = parseFloat(e.target.value); handleUpdate(acct.id, "pct_contract", isNaN(v) ? 0.005 : Math.min(1, Math.max(0, v))); } if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="text-right tabular-nums text-slate-700 font-medium cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: acct.id, field: "pct_contract" })} data-testid={`cell-pct-contract-${row.index}`}>
            {(val * 100).toFixed(1)}%
          </div>
        );
      },
      size: 90,
    },
    {
      id: "abs_revenue",
      header: ({ column }) => <SortHeader column={column} label="ABS Revenue" />,
      accessorFn: (row) => row.abs_revenue ?? 0,
      cell: ({ row }) => {
        const absRev = row.original.abs_revenue ?? 0;
        const rev = row.original.revenue ?? 0;
        return (
          <div className="text-right tabular-nums text-slate-700 font-semibold" data-testid={`cell-abs-revenue-${row.index}`}>
            {rev ? formatRevenue(absRev) : <span className="text-slate-400">--</span>}
          </div>
        );
      },
      size: 120,
    },
    {
      accessorKey: "notes",
      header: ({ column }) => <SortHeader column={column} label="Notes" />,
      cell: ({ row }) => {
        const acct = row.original;
        const isEditing = editingCell?.rowId === acct.id && editingCell?.field === "notes";
        if (isEditing) {
          return (
            <textarea
              defaultValue={acct.notes || ""}
              autoFocus
              className="w-full min-h-[72px] text-xs leading-snug px-2 py-1.5 border border-[#007BFF] rounded-md focus:outline-none focus:ring-1 focus:ring-[#007BFF] resize-y bg-white"
              data-testid={`notes-input-${row.index}`}
              onBlur={(e) => handleUpdate(acct.id, "notes", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleUpdate(acct.id, "notes", e.target.value); } if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div
            className="text-xs text-slate-700 leading-snug whitespace-pre-wrap break-words cursor-pointer hover:bg-blue-50/50 rounded px-1.5 py-1 -mx-1 transition-colors min-h-[28px]"
            onClick={() => setEditingCell({ rowId: acct.id, field: "notes" })}
            data-testid={`cell-notes-${row.index}`}
          >
            {acct.notes ? acct.notes : <span className="text-slate-400 italic">Add notes…</span>}
          </div>
        );
      },
      size: 360,
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => (
        <TooltipProvider>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-amber-600 p-1 rounded"
                  onClick={() => handleUnassignAccount(row.original.id)} data-testid={`unassign-account-${row.index}`}>
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Move to Unassigned</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 p-1 rounded"
                  onClick={() => handleDelete(row.original.id)} data-testid={`delete-account-${row.index}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Delete account</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ),
      size: 70,
    },
  ], [editingCell, managerNames]);

  // Unassigned accounts columns (same as assigned but with assign button)
  const unassignedColumns = useMemo(() => [
    {
      accessorKey: "client",
      header: ({ column }) => <SortHeader column={column} label="Client" />,
      cell: ({ row }) => {
        const acc = row.original;
        const isEditing = editingCell?.rowId === acc.id && editingCell?.field === "client";
        if (isEditing) {
          return (
            <Input defaultValue={acc.client} autoFocus className="h-7 text-sm" data-testid={`unassigned-client-input-${row.index}`}
              onBlur={(e) => handleUpdate(acc.id, "client", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUpdate(acc.id, "client", e.target.value); if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="font-medium text-slate-900 cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: acc.id, field: "client" })} data-testid={`unassigned-cell-client-${row.index}`}>
            {acc.client || <span className="text-slate-400 italic">Enter client name</span>}
          </div>
        );
      },
      size: 220,
    },
    {
      accessorKey: "account_manager",
      header: ({ column }) => <SortHeader column={column} label="AM" />,
      cell: ({ row }) => {
        const acc = row.original;
        const isEditing = editingCell?.rowId === acc.id && editingCell?.field === "account_manager";
        if (isEditing) {
          return (
            <Select defaultValue={acc.account_manager} onValueChange={(v) => handleUpdate(acc.id, "account_manager", v)}>
              <SelectTrigger className="h-7 text-sm w-full" data-testid={`unassigned-am-input-${row.index}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {managerNames.map((name, idx) => <SelectItem key={`${name}-${idx}`} value={name}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
          );
        }
        return (
          <div className="text-sm text-slate-700 cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: acc.id, field: "account_manager" })} data-testid={`unassigned-cell-am-${row.index}`}>
            {acc.account_manager || "Unassigned"}
          </div>
        );
      },
      size: 140,
    },
    {
      accessorKey: "rating",
      header: ({ column }) => <SortHeader column={column} label="Rating" />,
      cell: ({ row }) => {
        const acc = row.original;
        const isEditing = editingCell?.rowId === acc.id && editingCell?.field === "rating";
        if (isEditing) {
          return (
            <Select defaultValue={acc.rating || ""} onValueChange={(v) => handleUpdate(acc.id, "rating", v)}>
              <SelectTrigger className="h-7 text-sm w-20" data-testid={`unassigned-rating-input-${row.index}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {RATING_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          );
        }
        return acc.rating ? (
          <Badge variant="outline" className={cn("cursor-pointer font-medium", RATING_COLORS[acc.rating])}
            onClick={() => setEditingCell({ rowId: acc.id, field: "rating" })} data-testid={`unassigned-cell-rating-${row.index}`}>{acc.rating}</Badge>
        ) : (
          <span className="text-slate-400 cursor-pointer hover:text-slate-600" onClick={() => setEditingCell({ rowId: acc.id, field: "rating" })} data-testid={`unassigned-cell-rating-${row.index}`}>—</span>
        );
      },
      size: 80,
    },
    {
      accessorKey: "revenue",
      header: ({ column }) => <SortHeader column={column} label="GC Revenue" />,
      cell: ({ row }) => {
        const acc = row.original;
        const isEditing = editingCell?.rowId === acc.id && editingCell?.field === "revenue";
        if (isEditing) {
          return (
            <Input type="number" defaultValue={acc.revenue ?? ""} autoFocus className="h-7 text-sm w-full text-right" data-testid={`unassigned-revenue-input-${row.index}`}
              onBlur={(e) => handleUpdate(acc.id, "revenue", e.target.value ? parseFloat(e.target.value) : null)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUpdate(acc.id, "revenue", e.target.value ? parseFloat(e.target.value) : null); if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="text-right tabular-nums text-slate-700 cursor-pointer hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: acc.id, field: "revenue" })} data-testid={`unassigned-cell-revenue-${row.index}`}>
            {acc.revenue != null ? formatRevenue(acc.revenue) : <span className="text-slate-400">--</span>}
          </div>
        );
      },
      size: 130,
    },
    {
      accessorKey: "hit_rate",
      header: ({ column }) => <SortHeader column={column} label="Hit Rate" />,
      cell: ({ row }) => {
        const acc = row.original;
        const isEditing = editingCell?.rowId === acc.id && editingCell?.field === "hit_rate";
        const hrDisplay = ((acc.hit_rate ?? 0.25) * 100).toFixed(0);
        if (isEditing) {
          return (
            <Input type="number" defaultValue={hrDisplay} autoFocus className="h-7 text-sm w-16 text-right" data-testid={`unassigned-hitrate-input-${row.index}`}
              onBlur={(e) => handleUpdate(acc.id, "hit_rate", parseFloat(e.target.value) / 100)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUpdate(acc.id, "hit_rate", parseFloat(e.target.value) / 100); if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="text-right tabular-nums text-slate-600 cursor-pointer hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: acc.id, field: "hit_rate" })} data-testid={`unassigned-cell-hitrate-${row.index}`}>
            {hrDisplay}%
          </div>
        );
      },
      size: 90,
    },
    {
      accessorKey: "pct_contract",
      header: ({ column }) => <SortHeader column={column} label="% Contract" />,
      cell: ({ row }) => {
        const acc = row.original;
        const isEditing = editingCell?.rowId === acc.id && editingCell?.field === "pct_contract";
        const pcDisplay = ((acc.pct_contract ?? 0.005) * 100).toFixed(2);
        if (isEditing) {
          return (
            <Input type="number" step="0.01" defaultValue={pcDisplay} autoFocus className="h-7 text-sm w-16 text-right" data-testid={`unassigned-pctcontract-input-${row.index}`}
              onBlur={(e) => handleUpdate(acc.id, "pct_contract", parseFloat(e.target.value) / 100)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUpdate(acc.id, "pct_contract", parseFloat(e.target.value) / 100); if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="text-right tabular-nums text-slate-600 cursor-pointer hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: acc.id, field: "pct_contract" })} data-testid={`unassigned-cell-pctcontract-${row.index}`}>
            {pcDisplay}%
          </div>
        );
      },
      size: 100,
    },
    {
      id: "abs_revenue",
      header: ({ column }) => <SortHeader column={column} label="ABS Revenue" />,
      accessorFn: (row) => row.abs_revenue ?? 0,
      cell: ({ row }) => {
        const absRev = row.original.abs_revenue ?? 0;
        const rev = row.original.revenue ?? 0;
        return (
          <div className="text-right tabular-nums text-slate-700 font-semibold" data-testid={`unassigned-cell-abs-revenue-${row.index}`}>
            {rev ? formatRevenue(absRev) : <span className="text-slate-400">--</span>}
          </div>
        );
      },
      size: 130,
    },
    {
      accessorKey: "notes",
      header: ({ column }) => <SortHeader column={column} label="Notes" />,
      cell: ({ row }) => {
        const acc = row.original;
        const isEditing = editingCell?.rowId === acc.id && editingCell?.field === "notes";
        if (isEditing) {
          return (
            <textarea
              defaultValue={acc.notes || ""}
              autoFocus
              className="w-full min-h-[72px] text-xs leading-snug px-2 py-1.5 border border-amber-500 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 resize-y bg-white"
              data-testid={`unassigned-notes-input-${row.index}`}
              onBlur={(e) => handleUpdate(acc.id, "notes", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleUpdate(acc.id, "notes", e.target.value); } if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div
            className="text-xs text-slate-700 leading-snug whitespace-pre-wrap break-words cursor-pointer hover:bg-amber-50/50 rounded px-1.5 py-1 -mx-1 transition-colors min-h-[28px]"
            onClick={() => setEditingCell({ rowId: acc.id, field: "notes" })}
            data-testid={`unassigned-cell-notes-${row.index}`}
          >
            {acc.notes ? acc.notes : <span className="text-slate-400 italic">Add notes…</span>}
          </div>
        );
      },
      size: 360,
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => (
        <TooltipProvider>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-emerald-600 p-1 rounded"
                  onClick={() => handleAssignAccount(row.original.id)} data-testid={`assign-account-${row.index}`}>
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Move to Assigned</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 p-1 rounded"
                  onClick={() => handleDelete(row.original.id, true)} data-testid={`delete-unassigned-${row.index}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Delete account</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ),
      size: 70,
    },
  ], [editingCell, managerNames]);

  // No Bid List columns
  const noBidColumns = useMemo(() => [
    {
      accessorKey: "client",
      header: ({ column }) => <SortHeader column={column} label="Client" />,
      cell: ({ row }) => {
        const item = row.original;
        const isEditing = editingCell?.rowId === item.id && editingCell?.field === "client";
        if (isEditing) {
          return (
            <Input defaultValue={item.client || ""} autoFocus className="h-7 text-sm w-full" data-testid={`nobid-client-input-${row.index}`}
              onBlur={(e) => handleUpdateNoBid(item.id, "client", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUpdateNoBid(item.id, "client", e.target.value); if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="font-medium text-slate-900 cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: item.id, field: "client" })} data-testid={`nobid-cell-client-${row.index}`}>
            {item.client || <span className="text-slate-400 italic">Enter client name</span>}
          </div>
        );
      },
      size: 300,
    },
    {
      accessorKey: "reason",
      header: ({ column }) => <SortHeader column={column} label="Reason" />,
      cell: ({ row }) => {
        const item = row.original;
        const isEditing = editingCell?.rowId === item.id && editingCell?.field === "reason";
        if (isEditing) {
          return (
            <Input defaultValue={item.reason || ""} autoFocus className="h-7 text-sm w-full" data-testid={`nobid-reason-input-${row.index}`}
              onBlur={(e) => handleUpdateNoBid(item.id, "reason", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUpdateNoBid(item.id, "reason", e.target.value); if (e.key === "Escape") setEditingCell(null); }}
            />
          );
        }
        return (
          <div className="text-slate-700 cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: item.id, field: "reason" })} data-testid={`nobid-cell-reason-${row.index}`}>
            {item.reason || <span className="text-slate-400 italic">Enter reason</span>}
          </div>
        );
      },
      size: 500,
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 p-1 rounded"
                onClick={() => handleDeleteNoBid(row.original.id)} data-testid={`delete-nobid-${row.index}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Delete entry</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
      size: 50,
    },
  ], [editingCell]);

  const table = useReactTable({
    data: filteredAccounts,
    columns,
    state: { sorting, globalFilter: searchQuery },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const unassignedTable = useReactTable({
    data: filteredUnassignedAccounts,
    columns: unassignedColumns,
    state: { sorting: unassignedSorting, globalFilter: unassignedSearchQuery },
    onSortingChange: setUnassignedSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const noBidTable = useReactTable({
    data: filteredNoBidList,
    columns: noBidColumns,
    state: { sorting: noBidSorting, globalFilter: noBidSearchQuery },
    onSortingChange: setNoBidSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="accounts-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-[#007BFF] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-500">Loading accounts...</span>
        </div>
      </div>
    );
  }

  const VIEW = {
    assigned: { q: searchQuery, setQ: setSearchQuery, ph: "Search clients…", tid: "accounts-search",
                n: filteredAccounts.length, unit: "account", add: () => handleAddAccount(true), addLabel: "Account", addTid: "add-account-btn" },
    unassigned: { q: unassignedSearchQuery, setQ: setUnassignedSearchQuery, ph: "Search clients…", tid: "unassigned-search",
                  n: filteredUnassignedAccounts.length, unit: "account", add: () => handleAddAccount(false), addLabel: "Account", addTid: "add-unassigned-btn" },
    nobid: { q: noBidSearchQuery, setQ: setNoBidSearchQuery, ph: "Search the no-bid list…", tid: "nobid-search",
             n: filteredNoBidList.length, unit: "entr", add: handleAddNoBid, addLabel: "No Bid", addTid: "add-nobid-btn" },
  }[view];
  const countText = VIEW.unit === "entr" ? `${VIEW.n} entr${VIEW.n !== 1 ? "ies" : "y"}` : `${VIEW.n} account${VIEW.n !== 1 ? "s" : ""}`;

  return (
    <div className="page-pane gap-3" data-testid="accounts-page">
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="accounts-toolbar">
        <SearchBar key={view} value={VIEW.q} onChange={VIEW.setQ} placeholder={VIEW.ph} testId={VIEW.tid} />
        <Segmented
          options={[{ key: "assigned", label: "Assigned", testId: "accounts-view-assigned" },
                    { key: "unassigned", label: "Unassigned", testId: "accounts-view-unassigned" },
                    { key: "nobid", label: "No Bid", testId: "accounts-view-nobid" }]}
          value={view} onChange={setView}
        >
          {view === "assigned" && (<>
            <span className="mx-0.5 h-4 w-px bg-slate-300" />
            <Pill p={{ key: "accounts-am", label: "AM", options: managerNames.map((n) => ({ value: n, label: n })),
                       value: amFilter === "ALL" ? "" : amFilter, onChange: (v) => setAmFilter(v || "ALL") }} />
            <Pill p={{ key: "accounts-rating", label: "Ratings", options: RATING_OPTIONS.map((r) => ({ value: r, label: r })),
                       value: ratingFilter === "ALL" ? "" : ratingFilter, onChange: (v) => setRatingFilter(v || "ALL") }} />
          </>)}
        </Segmented>
        <Button size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={VIEW.add} data-testid={VIEW.addTid}>
          <Plus className="h-3.5 w-3.5" /> {VIEW.addLabel}
        </Button>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="text-slate-400 font-medium" data-testid="accounts-count">{countText}</span>
          {view !== "nobid" && (<>
            <span className="font-semibold text-blue-900" data-testid="accounts-total-gc">GC Rev: {formatShortMoney((view === "unassigned" ? unassignedTotals : totals).gcTotal)}</span>
            <span className="text-slate-300">|</span>
            <span className="font-semibold text-green-800" data-testid="accounts-total-abs">ABS Rev: {formatShortMoney((view === "unassigned" ? unassignedTotals : totals).absTotal)}</span>
          </>)}
        </div>
      </div>
      <div className="pane-card border border-slate-200 bg-white">
        <div className="flex-1 overflow-auto bg-white">
          {view === "assigned" && (
            <Table>
              <TableHeader className="sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                {table.getHeaderGroups().map(hg => (
                  <TableRow key={hg.id} className="border-b-2 border-slate-200 hover:bg-transparent">
                    {hg.headers.map(header => (
                      <TableHead key={header.id} className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 whitespace-nowrap"
                        style={{ width: header.getSize() }} data-testid={`account-col-${header.id}`}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length > 0 ? table.getRowModel().rows.map(row => (
                  <TableRow key={row.id} className="group hover:bg-slate-50/80 transition-colors border-b border-slate-100" data-testid={`account-row-${row.index}`}>
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="px-4 py-2.5" style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-48 text-center">
                      <div className="flex flex-col items-center gap-3" data-testid="accounts-empty">
                        <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center">
                          <Plus className="h-7 w-7 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-600">No accounts yet</p>
                          <p className="text-xs text-slate-400 mt-1">Click "Add Account" to start tracking clients</p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          {view === "unassigned" && (
            <Table>
              <TableHeader className="sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                {unassignedTable.getHeaderGroups().map(hg => (
                  <TableRow key={hg.id} className="border-b-2 border-slate-200 hover:bg-transparent">
                    {hg.headers.map(header => (
                      <TableHead key={header.id} className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 whitespace-nowrap"
                        style={{ width: header.getSize() }} data-testid={`unassigned-col-${header.id}`}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {unassignedTable.getRowModel().rows.length > 0 ? unassignedTable.getRowModel().rows.map(row => (
                  <TableRow key={row.id} className="group hover:bg-amber-50/50 transition-colors border-b border-slate-100" data-testid={`unassigned-row-${row.index}`}>
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="px-4 py-2.5" style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={unassignedColumns.length} className="h-48 text-center">
                      <div className="flex flex-col items-center gap-3" data-testid="unassigned-empty">
                        <div className="h-14 w-14 rounded-xl bg-amber-50 flex items-center justify-center">
                          <Plus className="h-7 w-7 text-amber-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-600">No unassigned accounts</p>
                          <p className="text-xs text-slate-400 mt-1">Add accounts here before assigning them to account managers</p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          {view === "nobid" && (
            <Table>
              <TableHeader className="sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                {noBidTable.getHeaderGroups().map(hg => (
                  <TableRow key={hg.id} className="border-b-2 border-slate-200 hover:bg-transparent">
                    {hg.headers.map(header => (
                      <TableHead key={header.id} className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 whitespace-nowrap"
                        style={{ width: header.getSize() }} data-testid={`nobid-col-${header.id}`}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {noBidTable.getRowModel().rows.length > 0 ? noBidTable.getRowModel().rows.map(row => (
                  <TableRow key={row.id} className="group hover:bg-red-50/50 transition-colors border-b border-slate-100" data-testid={`nobid-row-${row.index}`}>
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="px-4 py-2.5" style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={noBidColumns.length} className="h-48 text-center">
                      <div className="flex flex-col items-center gap-3" data-testid="nobid-empty">
                        <div className="h-14 w-14 rounded-xl bg-red-50 flex items-center justify-center">
                          <Ban className="h-7 w-7 text-red-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-600">No bid list is empty</p>
                          <p className="text-xs text-slate-400 mt-1">Click "Add No Bid Entry" to track clients you won't bid on</p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
